import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, X } from 'lucide-react';
import { Button } from './ui/button';
import { useEscapeKey } from '../hooks/useEscapeKey';

/**
 * Inline paywall card. Render conditionally when a feature is gated.
 * For modals over a CTA, use the `inModal` prop and pass `onClose`.
 */
export default function UpgradePrompt({
  feature,
  requiredPlan = 'basic',
  message,
  inModal = false,
  onClose,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEscapeKey(onClose, inModal && !!onClose);

  const planLabel = { basic: 'Basic', pro: 'Pro', max: 'Max', team: 'Team' }[requiredPlan] || 'Basic';
  const headline = message || (feature
    ? t('ui_common.upgrade.feature_requires_plan', { feature, plan: planLabel })
    : t('ui_common.upgrade.this_feature_requires_plan', { plan: planLabel }));

  const handleNavigate = (path) => {
    onClose?.();
    navigate(path);
  };

  const card = (
    <div className="relative rounded-xl border border-border bg-card shadow-lg p-5">
      {inModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          aria-label={t('common.close')}
        >
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-amber-500/15 p-2 shrink-0">
          <Crown className="w-5 h-5 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <h3 className="text-sm font-semibold text-foreground">{headline}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('ui_common.upgrade.unlock_premium')}
          </p>
          <div className="mt-3">
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-black"
              onClick={() => handleNavigate('/app/subscription')}
            >
              {t('ui_common.upgrade.view_plans')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!inModal) return card;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md">{card}</div>
    </div>
  );
}
